import api from "./api";

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || "";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window &&
    !!VAPID_PUBLIC_KEY
  );
}

export function pushPermission() {
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission; // "default" | "granted" | "denied"
}

export async function ensureServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  const existing = await navigator.serviceWorker.getRegistration("/sw.js");
  if (existing) return existing;
  return navigator.serviceWorker.register("/sw.js");
}

export async function subscribeToPush() {
  if (!isPushSupported()) throw new Error("Push notifications are not supported on this browser");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Permission denied for notifications");
  const reg = await ensureServiceWorker();
  if (!reg) throw new Error("Service worker registration failed");
  // Wait for sw to be active
  if (!reg.active) {
    await new Promise((resolve) => {
      const check = () => (reg.active ? resolve() : setTimeout(check, 100));
      check();
    });
  }
  const desiredKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
  let sub = await reg.pushManager.getSubscription();
  // If an existing subscription was created with a different VAPID public key
  // (very common when keys are rotated or were absent on first deploy), FCM will
  // silently reject every push. Tear it down and recreate with the current key.
  if (sub) {
    const existingKey = sub.options && sub.options.applicationServerKey;
    const matches = existingKey && new Uint8Array(existingKey).every((v, i) => v === desiredKey[i]) && existingKey.byteLength === desiredKey.byteLength;
    if (!matches) {
      try { await sub.unsubscribe(); } catch { /* noop */ }
      sub = null;
    }
  }
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: desiredKey,
    });
  }
  const payload = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: payload.endpoint,
    keys: payload.keys,
    user_agent: navigator.userAgent,
  });
  localStorage.setItem("newell:push_subscribed", "1");
  return sub;
}

export async function unsubscribeFromPush() {
  const reg = await navigator.serviceWorker.getRegistration("/sw.js");
  const sub = reg && (await reg.pushManager.getSubscription());
  if (sub) {
    const payload = sub.toJSON();
    try { await api.post("/push/unsubscribe", { endpoint: payload.endpoint, keys: payload.keys }); } catch { /* noop */ }
    await sub.unsubscribe();
  }
  localStorage.setItem("newell:push_subscribed", "0");
}

export function pushSubscribedLocal() {
  return typeof localStorage !== "undefined" && localStorage.getItem("newell:push_subscribed") === "1";
}

export function pushPromptDismissed() {
  return typeof localStorage !== "undefined" && localStorage.getItem("newell:push_prompt_dismissed") === "1";
}

export function dismissPushPrompt() {
  localStorage.setItem("newell:push_prompt_dismissed", "1");
}
